import moment from 'moment';
import Papa from 'papaparse';
import filePath from '../../csvFile/combined.csv';

// detectionDate in combined.csv is DD/MM/YYYY, which Date.parse reads as MM/DD/YYYY.
export const DATE_FORMAT = 'DD/MM/YYYY';

export const DETECTION_TYPES = [
    { key: 'Face Recognition', label: 'Faces Detected', color: '#2F80ED' },
    { key: 'Number Plate', label: 'Number Plates Detected', color: '#9B51E0' },
    { key: 'Baggage Detection', label: 'Baggage Detected', color: '#00A3A3' },
    { key: 'PPE Detection', label: 'PPE Detected', color: '#F2994A' },
    { key: 'FireSmoke Detection', label: 'Fire/Smoke Detected', color: '#EB5757' },
    { key: 'ForeignMaterial Detection', label: 'Foreign Material Detected', color: '#219653' },
];

export const DETECTION_TYPES_FILTER=[
    { key: 'Face Recognition', label: 'Faces' },
    { key: 'Number Plate', label: 'Number Plates' },
    { key: 'Baggage Detection', label: 'Baggage' },
    { key: 'PPE Detection', label: 'PPE' },
    { key: 'FireSmoke Detection', label: 'Fire/Smoke' },
    { key: 'ForeignMaterial Detection', label: 'Foreign Material' },
]
export const TIME_RANGES = [
    { key: 'all', label: 'No filter', days: null },
    { key: 'today', label: 'Today', days: 0 },
    { key: '7d', label: '7D', days: 7 },
    { key: '30d', label: '30D', days: 30 },
];

// export const SEVERITIES = [
//     { key: 'critical', label: 'Critical', color: '#EB5757' },
//     { key: 'warning', label: 'Warning', color: '#F2994A' },
//     { key: 'info', label: 'Info', color: '#2F80ED' },
// ];

// combined.csv has no zone column, so zones are derived from cameraName.
// Every camera in the CSV maps to exactly one zone; unmapped cameras fall into Unassigned.
export const ZONES = [
    { key: 'A', name: 'Zone A', description: 'Parking Entrance', cameras: ['Parking Entrance'] },
    { key: 'B', name: 'Zone B', description: 'Transit Terminal', cameras: ['Bus Entrance', 'Train Station'] },
    { key: 'C', name: 'Zone C', description: 'Industrial Floor', cameras: ['Warehouse', 'Factory', 'Kitchen'] },
    { key: 'D', name: 'Zone D', description: 'Highway Corridor', cameras: ['Highway'] },
];

const UNASSIGNED_ZONE = { key: 'unassigned', name: 'Unassigned', description: 'Camera not mapped to a zone', cameras: [] };

const CAMERA_TO_ZONE = ZONES.reduce((acc, zone) => {
    zone.cameras.forEach((camera) => { acc[camera.toLowerCase()] = zone.key; });
    return acc;
}, {});

export const zoneKeyForCamera = (cameraName) =>
    CAMERA_TO_ZONE[(cameraName || '').trim().toLowerCase()] || UNASSIGNED_ZONE.key;

const isTruthyFlag = (value) => value === true || value === 'True';
const isUnresolved = (name) => name === 'Not Detected' || name === 'Unknown';

// // combined.csv has no severity column either. Severity is derived from what the
// // detection actually says: an unattended weapon-flagged bag or a fire is critical,
// // a compliance/contamination hit is a warning, a clean recognition is informational.
// export const severityForRow = (row) => {
//     if (row.kpiType === 'FireSmoke Detection') return 'critical';
//     if (row.kpiType === 'Baggage Detection' && isTruthyFlag(row.hasWpn)) return 'critical';
//     if (row.kpiType === 'PPE Detection') return 'warning';
//     if (row.kpiType === 'ForeignMaterial Detection') return 'warning';
//     if (isUnresolved(row.detectionName)) return 'warning';
//     return 'info';
// };

const enrich = (row) => {
    const parsedDate = moment(row.detectionDate, DATE_FORMAT, true);
    return {
        ...row,
        zoneKey: zoneKeyForCamera(row.cameraName),
        // severity: severityForRow(row),
        date: parsedDate.isValid() ? parsedDate : null,
    };
};

export async function loadDetections() {
    const response = await fetch(filePath);
    const csvText = await response.text();
    const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
    return parsed.data.filter((row) => row.kpiType).map(enrich);
}

export function filterDetections(rows, { types = [], timeRange = 'all'}) {
    const range = TIME_RANGES.find((r) => r.key === timeRange) || TIME_RANGES[0];
    const cutoff = range.days === null ? null : moment().startOf('day').subtract(range.days, 'days');

    return rows.filter((row) => {
        if (types.length && !types.includes(row.kpiType)) return false;
        // if (severities.length && !severities.includes(row.severity)) return false;
        if (cutoff) {
            if (!row.date) return false;
            if (row.date.isBefore(cutoff)) return false;
        }
        return true;
    });
}

// The "highlight" of a detection type is the subset that actually matters
// operationally, which is what the sub-line under each KPI number reports.
const HIGHLIGHTS = {
    'Face Recognition': { test: (r) => !isUnresolved(r.detectionName), noun: 'people matched to a name' },
    'Number Plate': { test: (r) => !isUnresolved(r.detectionName), noun: 'plates recognised' },
    'Baggage Detection': { test: (r) => isTruthyFlag(r.hasWpn), noun: ' people carrying baggage' }
};

export function summarise(rows) {
    return DETECTION_TYPES.map((type) => {
        const forType = rows.filter((row) => row.kpiType === type.key);
        const highlight = HIGHLIGHTS[type.key];
        return {
            ...type,
            total: forType.length,
            highlightCount: highlight ? forType.filter(highlight.test).length : null,

            highlightNoun:  highlight ? highlight.noun : null,
        } ;
    });
}

export function zoneStats(rows, allRows) {
    const zones = [...ZONES];
    const hasUnassigned = allRows.some((row) => row.zoneKey === UNASSIGNED_ZONE.key);
    if (hasUnassigned) zones.push(UNASSIGNED_ZONE);

    return zones.map((zone) => {
        const forZone = rows.filter((row) => row.zoneKey === zone.key);
        // Camera count reflects the zone's full hardware footprint, not the filtered slice,
        // so it stays stable while the user changes filters.
        const cameras = [...new Set(
            allRows.filter((row) => row.zoneKey === zone.key).map((row) => row.cameraName)
        )];
        const activeTypes = DETECTION_TYPES.filter((type) =>
            forZone.some((row) => row.kpiType === type.key)
        );
        return {
            ...zone,
            detections: forZone.length,
            cameraCount: cameras.length,
            cameras,
            activeTypes,
            // critical: forZone.filter((row) => row.severity === 'critical').length,
        };
    });
}

// Detections in combined.csv are clustered on a handful of days across ~19 months,
// so a fixed daily axis would be almost entirely empty. Bucket by day only when the
// span is short enough for that to read; otherwise roll up to months.
const DAY_BUCKET_MAX_SPAN = 60;

const BUCKET_FORMATS = {
    day: { key: 'YYYY-MM-DD', axis: 'MMM D', tooltip: 'Do MMMM, YYYY' },
    month: { key: 'YYYY-MM', axis: 'MMM YYYY', tooltip: 'MMMM YYYY' },
};

export function buildTimeSeries(rows, activeTypes = []) {
    const series = DETECTION_TYPES.filter(
        (type) => !activeTypes.length || activeTypes.includes(type.key)
    );
    const dated = rows.filter((row) => row.date);
    if (!dated.length) return { buckets: [], series, granularity: 'day', max: 0 };

    const dates = dated.map((row) => row.date);
    const first = moment.min(dates);
    const last = moment.max(dates);
    const granularity = last.diff(first, 'days') <= DAY_BUCKET_MAX_SPAN ? 'day' : 'month';
    const format = BUCKET_FORMATS[granularity];

    // Walk every interval in the range, not just the ones with detections, so gaps
    // render as real zeroes rather than the line skipping across them.
    const buckets = [];
    const cursor = first.clone().startOf(granularity);
    const end = last.clone().startOf(granularity);
    while (cursor.isSameOrBefore(end)) {
        buckets.push({
            key: cursor.format(format.key),
            axisLabel: cursor.format(format.axis),
            tooltipLabel: cursor.format(format.tooltip),
            counts: series.reduce((acc, type) => ({ ...acc, [type.key]: 0 }), {}),
            total: 0,
        });
        cursor.add(1, granularity);
    }

    const byKey = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: bucket }), {});
    dated.forEach((row) => {
        const bucket = byKey[row.date.format(format.key)];
        if (!bucket || !(row.kpiType in bucket.counts)) return;
        bucket.counts[row.kpiType] += 1;
        bucket.total += 1;
    });

    const max = buckets.reduce(
        (acc, bucket) => Math.max(acc, ...series.map((type) => bucket.counts[type.key])),
        0
    );

    return { buckets, series, granularity, max };
}

// Alerts are the subset of detections that need somebody to look at them: a fire,
// a flagged bag, a compliance or contamination hit, or a subject nothing recognised.
// Everything else in the CSV is a clean read and never raises an alert.
const ALERT_RULES = [
    { test: (r) => r.kpiType === 'FireSmoke Detection', title: 'Fire / Smoke' },
    { test: (r) => r.kpiType === 'Baggage Detection' && isTruthyFlag(r.hasWpn), title: 'Baggage Carried' },
    { test: (r) => r.kpiType === 'PPE Detection', title: 'PPE Violation' },
    { test: (r) => r.kpiType === 'ForeignMaterial Detection', title: 'Foreign Material' },
    { test: (r) => r.kpiType === 'Face Recognition' && isUnresolved(r.detectionName), title: 'Unrecognised Face' },
    { test: (r) => r.kpiType === 'Number Plate' && isUnresolved(r.detectionName), title: 'Unrecognised Plate' },
];

// How many of the newest alerts start out active; the next slice starts out resolved
// so the Resolved tab is never empty on a fresh load.
const DEFAULT_ACTIVE_COUNT = 6;
const DEFAULT_RESOLVED_COUNT = 3;

const alertTimestamp = (row) => {
    if (!row.date) return null;
    const time = moment(row.startTime, 'HH:mm:ss', true);
    return time.isValid()
        ? row.date.clone().hour(time.hour()).minute(time.minute()).second(time.second())
        : row.date.clone();
};

// Derived purely from the loaded rows and split by position, so a reload always
// rebuilds the same default active/resolved sets — resolving is session-only.
export function buildAlerts(rows) {
    const candidates = rows
        .map((row, index) => {
            const rule = ALERT_RULES.find((r) => r.test(row));
            if (!rule) return null;
            const zone = ZONES.find((z) => z.key === row.zoneKey);
            return {
                id: `${row.kpiType}-${row.trackId}-${index}`,
                title: rule.title,
                kpiType: row.kpiType,
                cameraName: row.cameraName || 'Unknown camera',
                zoneName: zone ? zone.name : UNASSIGNED_ZONE.name,
                detectionName: row.detectionName,
                at: alertTimestamp(row),
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            if (!a.at) return 1;
            if (!b.at) return -1;
            return b.at.valueOf() - a.at.valueOf();
        });

    return {
        active: candidates.slice(0, DEFAULT_ACTIVE_COUNT),
        resolved: candidates.slice(DEFAULT_ACTIVE_COUNT, DEFAULT_ACTIVE_COUNT + DEFAULT_RESOLVED_COUNT),
    };
}

export function latestDetectionDate(rows) {
    const dates = rows.map((row) => row.date).filter(Boolean);
    if (!dates.length) return null;
    return moment.max(dates);
}
